/**
 * NewChangeOrderPage — PT-43.1 / 43.3
 *
 * Route: /jobs/:jobId/change-orders/new?fromAcceptedQuote=1&sourceQuoteId=Q-000123
 *
 * Purpose: the exit-affordance target. When a user hits the PT-42 "document_locked"
 * 409 on an accepted quote, the confirm dialog offers "Create Change Order" — this
 * component IS that destination.
 *
 * Flow:
 *   1. On mount, POST /v1/change-orders with { job_id, source_quote_id, from_accepted_quote }.
 *   2. On success, redirect to /jobs/:jobId/change-orders/:coId/build — the standard
 *      builder route handles the rest. No state trick; the URL updates and the route
 *      change re-renders the builder cleanly.
 *   3. On failure, render an INLINE error with a "← Back to Job" link. No toasts;
 *      a failed navigation must be visible on the page it failed to leave.
 *
 * Belt-and-braces:
 *   - useRef guard prevents StrictMode double-mount from creating two COs.
 *   - The "already redirected" guard prevents the effect firing on a stale render.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { SkeletonCard } from '../ui/Skeleton';
import { Alert } from '../ui/Alert';
import api from '../../services/api';
import { parseApiError } from '../../utils/parseApiError';

export default function NewChangeOrderPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const createdRef = useRef(false);

  const fromAcceptedQuote = searchParams.get('fromAcceptedQuote') === '1';
  const sourceQuoteId = searchParams.get('sourceQuoteId') || '';

  useEffect(() => {
    // StrictMode + effect-double-fire guard. A duplicate CO would be a silent
    // data-quality bug — the second one would sit orphaned in the table.
    if (createdRef.current) return;
    createdRef.current = true;

    let cancelled = false;

    (async () => {
      try {
        const payload = {
          job_id: jobId,
          jobId: jobId, // backend accepts either; belt-and-suspenders
        };
        if (fromAcceptedQuote) payload.from_accepted_quote = true;
        if (sourceQuoteId) payload.source_quote_id = sourceQuoteId;

        const co = await api.createChangeOrder(payload);
        if (cancelled) return;

        const coId = co && (co.id || co.co_id);
        if (!coId) {
          setError('Change order was created but the server did not return an ID. Please refresh the job page and try again.');
          return;
        }

        // Redirect — replace: true so the browser back button skips this
        // transient route and returns to whatever the user came from.
        navigate(`/jobs/${jobId}/change-orders/${coId}/build`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const parsed = parseApiError(err);
        // Don't retry — if the CO couldn't be created, showing "Create Change
        // Order" on a subsequent visit isn't going to help. Fail loud.
        setError(
          parsed.reason ||
            (parsed.status === 404
              ? 'That job could not be found. It may have been deleted.'
              : 'Failed to create the change order. Please return to the job and try again from the Create menu.')
        );
      }
    })();

    return () => { cancelled = true; };
  }, [jobId, fromAcceptedQuote, sourceQuoteId, navigate]);

  if (error) {
    return (
      <div className="px-6">
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
        <Link
          to={`/jobs/${jobId}`}
          className="inline-flex items-center text-sm underline text-red-600 hover:text-red-800"
        >
          ← Back to Job
        </Link>
      </div>
    );
  }

  return (
    <div className="px-6">
      <SkeletonCard />
    </div>
  );
}
